import { useQuery } from "@tanstack/react-query";

import { SoccerMatchStatType } from "../../../types";
import { getMatchStats } from "../api";
import { pollIntervalMs } from "../utils/refreshCadence";

export const useMatchStats = (matchId?: string, kickoffTime?: string) => {
  const { error, data, isLoading, refetch } = useQuery({
    queryKey: [`match-stats-${matchId}`],
    queryFn: () => getMatchStats(matchId as string),
    enabled: !!matchId?.length,
    staleTime: 0,
    refetchInterval: () => pollIntervalMs("matchStats", kickoffTime),
    gcTime: 60000,
  });

  const teamAstats = data?.matchStats?.[0]?.stats ?? {};
  const teamBstats = data?.matchStats?.[1]?.stats ?? {};

  const parseFloatValue = (value?: string) => parseFloat(value ?? "0");

  const statsData: {
    teamA: {
      [key in SoccerMatchStatType]?: number;
    };
    teamB: {
      [key in SoccerMatchStatType]?: number;
    };
  } = {
    teamA: {
      [SoccerMatchStatType.SHOTS]: parseFloatValue(teamAstats.shots?.value),
      [SoccerMatchStatType.SHOTS_ON_TARGET]: parseFloatValue(teamAstats.shots_on_target?.value),
      [SoccerMatchStatType.SHOTS_INSIDE_BOX]: parseFloatValue(teamAstats.shots_inside_box?.value),
      [SoccerMatchStatType.BALL_POSSESSION]: parseFloatValue(teamAstats.ball_possession?.value),
      [SoccerMatchStatType.TOTAL_PASS]: parseFloatValue(teamAstats.total_pass?.value),
      [SoccerMatchStatType.ACCURATE_PASS]: parseFloatValue(teamAstats.accurate_pass?.value),
      [SoccerMatchStatType.PASSING_ACCURACY]: parseFloatValue(teamAstats.passing_accuracy?.value),
      [SoccerMatchStatType.FOULS]: parseFloatValue(teamAstats.fouls?.value),
      [SoccerMatchStatType.YELLOW_CARD]: parseFloatValue(teamAstats.yellow_card?.value),
      [SoccerMatchStatType.RED_CARD]: parseFloatValue(teamAstats.red_card?.value),
      [SoccerMatchStatType.CORNERS]: parseFloatValue(teamAstats.corners?.value),
      [SoccerMatchStatType.OFFSIDE]: parseFloatValue(teamAstats.offside?.value),
      [SoccerMatchStatType.WON_TACKLE]: parseFloatValue(teamAstats.won_tackle?.value),
      [SoccerMatchStatType.TOTAL_TACKLE]: parseFloatValue(teamAstats.total_tackle?.value),
      [SoccerMatchStatType.INTERCEPTIONS]: parseFloatValue(teamAstats.interceptions?.value),
      [SoccerMatchStatType.BLOCKED_SCORING_ATT]: parseFloatValue(teamAstats.blocked_scoring_att?.value),
      [SoccerMatchStatType.TOTAL_CLEARANCE]: parseFloatValue(teamAstats.total_clearance?.value),
      [SoccerMatchStatType.SAVES]: parseFloatValue(teamAstats.saves?.value),
      [SoccerMatchStatType.PENALTY_CONCEDED]: parseFloatValue(teamAstats.penalty_conceded?.value),
      [SoccerMatchStatType.PENALTY_KICKS]: parseFloatValue(teamAstats.penalty_kicks?.value),
    },
    teamB: {
      [SoccerMatchStatType.SHOTS]: parseFloatValue(teamBstats.shots?.value),
      [SoccerMatchStatType.SHOTS_ON_TARGET]: parseFloatValue(teamBstats.shots_on_target?.value),
      [SoccerMatchStatType.SHOTS_INSIDE_BOX]: parseFloatValue(teamBstats.shots_inside_box?.value),
      [SoccerMatchStatType.BALL_POSSESSION]: parseFloatValue(teamBstats.ball_possession?.value),
      [SoccerMatchStatType.TOTAL_PASS]: parseFloatValue(teamBstats.total_pass?.value),
      [SoccerMatchStatType.ACCURATE_PASS]: parseFloatValue(teamBstats.accurate_pass?.value),
      [SoccerMatchStatType.PASSING_ACCURACY]: parseFloatValue(teamBstats.passing_accuracy?.value),
      [SoccerMatchStatType.FOULS]: parseFloatValue(teamBstats.fouls?.value),
      [SoccerMatchStatType.YELLOW_CARD]: parseFloatValue(teamBstats.yellow_card?.value),
      [SoccerMatchStatType.RED_CARD]: parseFloatValue(teamBstats.red_card?.value),
      [SoccerMatchStatType.CORNERS]: parseFloatValue(teamBstats.corners?.value),
      [SoccerMatchStatType.OFFSIDE]: parseFloatValue(teamBstats.offside?.value),
      [SoccerMatchStatType.WON_TACKLE]: parseFloatValue(teamBstats.won_tackle?.value),
      [SoccerMatchStatType.TOTAL_TACKLE]: parseFloatValue(teamBstats.total_tackle?.value),
      [SoccerMatchStatType.INTERCEPTIONS]: parseFloatValue(teamBstats.interceptions?.value),
      [SoccerMatchStatType.BLOCKED_SCORING_ATT]: parseFloatValue(teamBstats.blocked_scoring_att?.value),
      [SoccerMatchStatType.TOTAL_CLEARANCE]: parseFloatValue(teamBstats.total_clearance?.value),
      [SoccerMatchStatType.SAVES]: parseFloatValue(teamBstats.saves?.value),
      [SoccerMatchStatType.PENALTY_CONCEDED]: parseFloatValue(teamBstats.penalty_conceded?.value),
      [SoccerMatchStatType.PENALTY_KICKS]: parseFloatValue(teamBstats.penalty_kicks?.value),
    },
  };
  return {
    error: error,
    data: statsData,
    isLoading,
    refetch,
  };
};
